import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABEL } from "@/lib/api";
import { StatusPill } from "@/components/Pills";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function Sponsor() {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([
          api.get("/requests"),
          api.get("/portfolio/summary"),
        ]);
        setRequests(r.data.requests || []);
        setSummary(s.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const open = requests.filter((r) => !["released", "rejected"].includes(r.status));
  const next = open.find((r) => r.status === "approved_for_build") || open[0];

  return (
    <div data-testid="sponsor-page">
      <section className="bc-rise">
        <p className="eyebrow eyebrow-signal">Sponsor view</p>
        <h1 className="font-display text-[44px] sm:text-[54px] tracking-tight leading-[1.05] mt-3" data-testid="sponsor-title">
          Your delivery, governed.
        </h1>
        <p className="mt-5 max-w-3xl text-[16px] leading-[1.65] text-[hsl(var(--bc-ink-soft))]">
          The questions a sponsor needs answered before approving a build — answered in one place.
          What is being built? Why does it matter? What already exists? Who owns it? What will it cost?
          What proves it was delivered?
        </p>
      </section>

      {/* SPONSOR QUESTIONS PANEL */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 bc-rise bc-rise-d1" data-testid="sponsor-questions">
        {[
          ["What is being built?", `${open.length} open build requests across the platform.`],
          ["What will it cost?", summary ? `Budget bands: ${Object.entries(summary.budget_bands).map(([k,v]) => `${k} × ${v}`).join(" · ")}` : "—"],
          ["What proves it was delivered?", summary ? `${summary.receipts_signed} signed receipt(s); coverage tracked at portfolio level.` : "—"],
        ].map(([q, a]) => (
          <div key={q} className="bc-card p-7">
            <p className="eyebrow mb-3">Question</p>
            <p className="font-display text-[22px] leading-snug">{q}</p>
            <p className="text-sm mt-4 text-[hsl(var(--bc-ink-soft))]">{a}</p>
          </div>
        ))}
      </section>

      {/* NEXT ACTION */}
      {next && (
        <section className="mt-14 border-y border-[hsl(var(--border))] py-10 bc-rise bc-rise-d2" data-testid="next-action">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-2">
              <p className="eyebrow">Next on your desk</p>
              <p className="font-mono text-sm mt-2">{next.request_id}</p>
            </div>
            <div className="md:col-span-7">
              <div className="font-display text-[24px] leading-snug">{next.requested_module}</div>
              <p className="text-sm mt-2 text-[hsl(var(--bc-muted))] max-w-2xl">{next.business_purpose}</p>
            </div>
            <div className="md:col-span-3 flex md:justify-end items-center gap-3">
              <StatusPill status={next.status} />
              <Link to={`/request/${next.request_id}`} className="bc-btn" data-testid="next-action-open">
                Open <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* OPEN REQUESTS */}
      <section className="mt-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="eyebrow">Open requests</p>
            <h2 className="font-display text-[28px] tracking-tight mt-2">Sponsor desk</h2>
          </div>
          <Link to="/portfolio" className="bc-btn-text">View portfolio →</Link>
        </div>
        {loading ? (
          <div className="text-sm text-[hsl(var(--bc-muted))]">Loading…</div>
        ) : (
          <div className="bc-card-flat divide-y divide-[hsl(var(--border))]" data-testid="sponsor-request-list">
            {open.map((r) => (
              <Link
                key={r.id}
                to={`/request/${r.request_id}`}
                className="grid grid-cols-12 gap-4 p-5 hover:bg-[hsl(var(--bc-paper))] transition-colors group"
                data-testid={`sponsor-row-${r.request_id}`}
              >
                <div className="col-span-2 font-mono text-sm">{r.request_id}</div>
                <div className="col-span-5">
                  <div className="text-[15px] font-medium">{r.requested_module}</div>
                  <div className="text-xs text-[hsl(var(--bc-muted))] mt-1 line-clamp-1">{r.business_purpose}</div>
                </div>
                <div className="col-span-2 text-xs text-[hsl(var(--bc-ink-soft))]">{r.estimated_budget_band}</div>
                <div className="col-span-2"><StatusPill status={r.status} /></div>
                <div className="col-span-1 flex justify-end text-[hsl(var(--bc-muted))] group-hover:text-[hsl(var(--bc-signal))]">
                  <ArrowUpRight size={18} />
                </div>
              </Link>
            ))}
            {open.length === 0 && <div className="p-6 text-sm text-[hsl(var(--bc-muted))]">No open requests.</div>}
          </div>
        )}
      </section>
    </div>
  );
}
